export const REPORT_SEARCH_MIN_LENGTH = 3;

export const getReportSearchHelperText = (
  query: string,
  matchCount: number,
  totalCount: number,
): string => {
  const trimmed = query.trim();
  if (!trimmed) return "";
  if (trimmed.length < REPORT_SEARCH_MIN_LENGTH) {
    const remaining = REPORT_SEARCH_MIN_LENGTH - trimmed.length;
    return `Enter at least ${REPORT_SEARCH_MIN_LENGTH} characters to search (${remaining} more needed)`;
  }
  if (matchCount === 0) {
    return "No rows match your search";
  }
  if (matchCount < totalCount) {
    return `Showing ${matchCount} of ${totalCount} row${totalCount !== 1 ? "s" : ""}`;
  }
  return "";
};

export const isReportSearchActive = (query: string): boolean =>
  query.trim().length >= REPORT_SEARCH_MIN_LENGTH;

export const filterReportRowsBySearch = <T>(
  rows: T[],
  query: string,
): T[] => {
  if (!Array.isArray(rows)) return rows;
  const trimmed = query.trim();
  if (!isReportSearchActive(trimmed)) return rows;

  const normalizedQuery = trimmed.toLowerCase();
  return rows.filter((row) => {
    if (!row || typeof row !== "object") return false;
    return Object.values(row as Record<string, unknown>).some((value) => {
      if (value == null) return false;
      if (typeof value === "object") {
        try {
          return JSON.stringify(value).toLowerCase().includes(normalizedQuery);
        } catch {
          return false;
        }
      }
      return String(value).toLowerCase().includes(normalizedQuery);
    });
  });
};

/** Segments without transaction-level DP Name on reports. */
export const DP_NAME_FILTER_EXCLUDED_SEGMENTS = new Set([
  "Insurance",
  "Other Asset",
]);

export const reportTypeSupportsDpNameFilter = (reportType?: string): boolean => {
  const rt = String(reportType ?? "").trim();
  return rt === "Realized" || rt === "All" || rt === "Transaction";
};

export const segmentSupportsDpNameFilter = (segment?: string): boolean =>
  Boolean(segment) && !DP_NAME_FILTER_EXCLUDED_SEGMENTS.has(segment ?? "");

export const normalizeRowDpName = (row: Record<string, unknown>): string => {
  const raw = row.dpName;
  if (raw === null || raw === undefined) return "";
  if (typeof raw === "string") return raw.trim();
  return String(raw).trim();
};

export const getUniqueDpNamesFromReportRows = (
  rows: unknown[],
): string[] => {
  if (!Array.isArray(rows)) return [];
  const names = new Set<string>();
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const name = normalizeRowDpName(row as Record<string, unknown>);
    if (name) names.add(name);
  }
  return Array.from(names).sort((a, b) => a.localeCompare(b));
};

export const filterReportRowsByDpName = <T>(
  rows: T[],
  selectedDpName: string,
): T[] => {
  if (!Array.isArray(rows)) return rows;
  const trimmed = selectedDpName.trim();
  if (!trimmed) return rows;
  return rows.filter((row) => {
    if (!row || typeof row !== "object") return false;
    return normalizeRowDpName(row as Record<string, unknown>) === trimmed;
  });
};
